import { useMutation, useQueryClient } from '@tanstack/vue-query'
import fetchApi from '@/utils/fetchApi'
import type { ConfigurationsListResult } from './useConfigurationsListQuery'

interface DeleteConfigurationParams {
  id: string
}

export function useDeleteConfigurationMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id }: DeleteConfigurationParams) => {
      await fetchApi({
        endpoint: `/api/configurations/delete/${encodeURIComponent(id)}`,
        method: 'DELETE',
      })

      return { id }
    },
    onMutate: async ({ id }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['configurations', 'list'] })

      // Snapshot all matching queries
      const previousQueries = queryClient.getQueriesData<ConfigurationsListResult>({
        queryKey: ['configurations', 'list'],
      })

      // Optimistically remove the item from all matching queries
      queryClient.setQueriesData<ConfigurationsListResult>(
        { queryKey: ['configurations', 'list'] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            configs: old.configs.filter((config) => config.id !== id),
            pagination: {
              ...old.pagination,
              total_hits: (old.pagination.total_hits ?? 0) - 1,
            },
          }
        },
      )

      return { previousQueries }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    },
    onSettled: () => {
      // Sync with server
      void queryClient.invalidateQueries({
        queryKey: ['configurations', 'list'],
        refetchType: 'active',
      })
      void queryClient.invalidateQueries({
        queryKey: ['configurations', 'languages'],
        refetchType: 'active',
      })
    },
  })
}
